import { useQuery, useMutation } from "@tanstack/react-query";
import { Get, Post } from "@/src/api";
import { useAuthStore } from "@/src/store/auth";

interface SignInCredentials {
  mail: string;
  password: string;
}

interface SignInResponse {
  jwtToken: string;
  accountManagerId: number;
}

interface UserOverview {
  id: number;
  firstName: string;
  lastName: string;
  mail: string;
  isAdmin: boolean;
}

export const useAuth = () => {
  const { token, user, setToken, setUser, logout } = useAuthStore();

  const signInMutation = useMutation({
    mutationFn: async (credentials: SignInCredentials) => {
      try {
        const response = await Post("/support/signin", credentials);
        return response as SignInResponse;
      } catch (error) {
        console.error("Sign in error:", error);
        throw new Error(
          error instanceof Error ? error.message : "Failed to sign in",
        );
      }
    },
    onSuccess: async (data, credentials) => {
      try {
        setToken(data.jwtToken);
        setUser({
          mail: credentials.mail,
          id: data.accountManagerId,
          firstName: "",
          lastName: "",
          isAdmin: false,
        });
        await getUserOverview();
        return data;
      } catch (error) {
        console.error("Error in onSuccess:", error);
        logout();
        throw error;
      }
    },
    onError: (error: Error) => {
      console.error("Sign in mutation error:", error);
      logout();
      throw error;
    },
  });

  const getUserOverview = async () => {
    try {
      const userOverview = await Get(`/support/auth/me`);
      setUser(userOverview as UserOverview);
      return userOverview;
    } catch (error) {
      console.error("Failed to fetch user overview:", error);
      logout();
      throw new Error("Failed to fetch user details");
    }
  };

  useQuery({
    queryKey: ["userOverview", user?.id],
    queryFn: () => (user?.id ? getUserOverview() : null),
    enabled: !!user?.id,
    retry: false,
  });

  return {
    user,
    token,
    isAuthenticated: !!token && !!user,
    signIn: (credentials: SignInCredentials) =>
      signInMutation.mutateAsync(credentials),
    isSigningIn: signInMutation.isPending,
    signInError: signInMutation.error as Error | null,
    logout,
  };
};
